import { Logger } from "../../helpers";
import {
  trackFacebookPixelEvent,
  trackTiktokPixelEvent,
  trackPinterestPixelEvent,
  trackInMarketEvent,
} from "./index";
import { GoogleAnalyticsTracker } from "./googleAnalytics";
import { TRACKERS } from "../config/trackingFlags";

/**
 * @fileoverview Manages client-side event tracking across various platforms.
 * @module ClientTrackerManager
 */
class ClientTrackerManager {
  constructor() {
    this.eventQueue = new Set(); // Use Set to prevent duplicate events
    this.googleAnalyticsTracker = null;
  }

  /**
   * Initializes the client-side tracking manager.
   * Conditionally initializes Google Analytics if enabled.
   */
  initialize() {
    if (TRACKERS.GOOGLE) {
      this.googleAnalyticsTracker = new GoogleAnalyticsTracker(this.hasUserConsent());

      // Update Google Analytics consent when changed
      window.addEventListener("consent-updated", () => {
        this.googleAnalyticsTracker.updateConsent(this.hasUserConsent());
      });

      Logger.log("Google Analytics initialized");
    }

    Logger.log("Client-side tracking initialized");
  }

  /**
   * Checks if the user has given tracking consent.
   * @returns {boolean} - True if the user has consented, false otherwise.
   */
  hasUserConsent() {
    const consentCookie = document.cookie.split("; ").find(row => row.startsWith("consentData="));
    if (!consentCookie) return false;

    try {
      const consentData = JSON.parse(decodeURIComponent(consentCookie.split("=")[1]));
      return consentData.hasOptedOut === false; // Ensure opt-out is respected
    } catch (error) {
      Logger.error("Error parsing consent data cookie:", error);
      return false;
    }
  }

  /**
   * Tracks an event on the client side.
   * Only sends to platforms that are both enabled and consented.
   *
   * @param {string} eventName - The event name to track (key from EVENTS).
   * @param {Object} eventData - The event data to send.
   * @param {Array|string} platforms - The platforms to send the event to. Defaults to "all".
   */
  trackEvent(eventName, eventData, platforms = "all") {
    Logger.log(`ClientTrackerManager: Attempting to track event "${eventName}"`, eventData);

    if (!this.hasUserConsent()) {
      Logger.warn(`⚠ User has opted out. Skipping tracking for event: ${eventName}`);
      return;
    }

    platforms = platforms === "all"
      ? ["googleAnalytics", "facebookPixel", "tiktokPixel", "pinterestPixel", "inMarket"]
      : (Array.isArray(platforms) ? platforms : [platforms]);

    Logger.log(`Sending event "${eventName}" to platforms:`, platforms);

    for (const platform of platforms) {
      switch (platform) {
        case "googleAnalytics":
          if (TRACKERS.GOOGLE) {
            Logger.log(`Google Analytics tracking: ${eventName}`);
            this.googleAnalyticsTracker?.trackEvent(eventName, eventData);
            
          }
          break;
        case "facebookPixel":
          if (TRACKERS.FACEBOOK) {
            Logger.log(`Facebook Pixel tracking: ${eventName}`);
            trackFacebookPixelEvent(eventName, eventData);
          }
          break;
        case "tiktokPixel":
          if (TRACKERS.TIKTOK) {
            Logger.log(`TikTok Pixel tracking: ${eventName}`);
            trackTiktokPixelEvent(eventName, eventData);
          }
          break;
        case "pinterestPixel":
          if (TRACKERS.PINTEREST) {
            Logger.log(`Pinterest Pixel tracking: ${eventName}`);
            trackPinterestPixelEvent(eventName, eventData);
          }
          break;
        case "inMarket":
          if (TRACKERS.INMARKET) {
            Logger.log(`InMarket tracking: ${eventName}`);
            trackInMarketEvent(eventName, eventData);
          }
          break;
        default:
          Logger.warn(`Unknown tracking platform: ${platform}`);
      }
    }

    Logger.log(`Event "${eventName}" successfully processed.`);
  }
}

export default ClientTrackerManager;
